import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Info } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useLoyaltyRangeMutations } from '~/hooks/use-loyalty-ranges';
import { loyaltyRangeSchema, EMPTY_LOYALTY_RANGE, type LoyaltyRangeFormValues } from '~/lib/schemas/loyalty-range';
import { dayEndIso, dayStartIso, toDayInput } from '~/lib/loyalty-dates';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: LoyaltyRangeFormValues | null;
}


/**
 * Add / edit a loyalty points range (Εύρος πόντων). On edit only the minimum-order-amount stays
 * editable — points, discount and the active dates are locked (legacy parity: the server upserts
 * ranges keyed by points, so changing them would create a different range instead of editing this one).
 */
export function LoyaltyRangeEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useLoyaltyRangeMutations();
	const form = useForm<LoyaltyRangeFormValues>({
		resolver: zodResolver(loyaltyRangeSchema) as Resolver<LoyaltyRangeFormValues>,
		defaultValues: initial ?? EMPTY_LOYALTY_RANGE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);
	const afrom = watch('afrom');
	const ato = watch('ato');

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_LOYALTY_RANGE);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('loyalty.ranges.editTitle') : t('loyalty.ranges.addTitle')}</DialogTitle>
					<DialogDescription>{t('loyalty.ranges.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<div className="grid grid-cols-2 gap-3">
						<FormField label={t('loyalty.ranges.columns.points')} error={formState.errors.pnt?.message}>
							<Input
								type="number"
								step={1}
								min={1}
								{...register('pnt', { valueAsNumber: true })}
								disabled={isEdit}
								aria-invalid={Boolean(formState.errors.pnt)}
								autoFocus={!isEdit}
							/>
						</FormField>
						<FormField label={t('loyalty.ranges.columns.discount')} error={formState.errors.amn?.message}>
							<Input
								type="number"
								step="any"
								{...register('amn', { valueAsNumber: true })}
								disabled={isEdit}
								aria-invalid={Boolean(formState.errors.amn)}
							/>
						</FormField>
					</div>
					<FormField label={t('loyalty.ranges.columns.minimumAmount')} error={formState.errors.mamn?.message}>
						<Input
							type="number"
							step="any"
							{...register('mamn', { setValueAs: v => (v === '' || v == null ? null : Number(v)) })}
							aria-invalid={Boolean(formState.errors.mamn)}
							autoFocus={isEdit}
						/>
					</FormField>
					<div className="grid grid-cols-2 gap-3">
						<FormField label={t('loyalty.ranges.columns.activeFrom')} error={formState.errors.afrom?.message}>
							<Input
								type="date"
								value={toDayInput(afrom)}
								onChange={e => setValue('afrom', dayStartIso(e.target.value, new Date()) ?? '', { shouldDirty: true, shouldValidate: true })}
								disabled={isEdit}
							/>
						</FormField>
						<FormField label={t('loyalty.ranges.columns.activeTo')} error={formState.errors.ato?.message}>
							<Input
								type="date"
								value={toDayInput(ato)}
								onChange={e => setValue('ato', dayEndIso(e.target.value), { shouldDirty: true, shouldValidate: true })}
								disabled={isEdit}
							/>
						</FormField>
					</div>
					<p className="flex items-start gap-2 rounded-md border border-border bg-elevated px-3 py-2 text-xs text-foreground-muted">
						<Info size={14} className="mt-0.5 shrink-0" />
						{t('loyalty.ranges.guide')}
					</p>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
